// pkg72a — green→navy conversion: decorative dark-green surfaces (#1a3a2a/#2a5a4a — banner gradients, featured borders, buttons, toggles, tabs) become NAVY_DEEP #0F1838 → NAVY #1E2761; SEMANTIC greens kept (status/success badges, category chip triads, ✓ text); selected-module card converts as a pair (navy border + pale-navy #F0F2F8 tint) — delivered 2026-07-12
// pkg72 — LawMighty retheme: brand hex sweep (#1a1a1a→#0F1838 NAVY_DEEP, #0f0f0f→#0A1028, #f8f8f8→#F5F1E8 CREAM, #c0392b→#B85042 TERRA) — delivered 2026-07-12
// pkg51 — wheel-scroll can no longer change a number input's value (a scroll notch = one $0.01 step was how $300.00 became $299.98): every type="number" input blurs on wheel — delivered 2026-07-09
// pkg49 — rate-change scope prompt (D-52b): changing a default rate asks who it applies to — new clients only (existing clients are stamped at the old rate), everyone currently at the old default, or a hand-picked list — delivered 2026-07-09
import React, { useState, useEffect } from 'react';
import { adminApi } from '../utils/adminApi';

const PERMISSIONS = [
  { key: 'view_clients',        label: 'View Clients',            desc: 'See the client list and client detail pages' },
  { key: 'edit_clients',        label: 'Edit Clients',            desc: 'Edit client info, add dates, links, notes' },
  { key: 'reset_passwords',     label: 'Reset Client Passwords',  desc: 'Reset portal passwords for clients' },
  { key: 'send_welcome_email',  label: 'Send Welcome Emails',     desc: 'Send welcome emails to clients' },
  { key: 'view_billing',        label: 'View Billing',            desc: 'See billing entries and hourly rates' },
  { key: 'edit_billing',        label: 'Edit Billing',            desc: 'Add and delete billing entries' },
  { key: 'generate_discovery',  label: 'Generate Discovery',      desc: 'Generate interrogatories and RFP documents' },
  { key: 'import_clio',         label: 'Clio Import',             desc: 'Import matters, contacts, and activities from Clio' },
  { key: 'manage_contacts',     label: 'Contacts',                desc: 'Import personal/professional contacts from Google or Apple' },
  { key: 'run_conflict_check',  label: 'Run Conflict Check',      desc: 'Run conflict checks against clients and contacts' },
  { key: 'mass_email',          label: 'Mass Email',              desc: 'Send emails to multiple clients at once' },
  { key: 'firm_dates',          label: 'Firm-Wide Dates',         desc: 'Add and manage dates visible to all clients' },
  { key: 'view_admin_data',     label: 'View Client Submissions', desc: 'View what clients have saved and submitted' },
  { key: 'manage_staff',        label: 'Manage Staff',            desc: "Access Staff Management and edit other users' permissions" },
];

export default function AdminUserPermissionsModal({ user, onClose, onUserUpdated, onUserDeleted }) {
  const [activeTab, setActiveTab] = useState('profile');
  const [currentUser, setCurrentUser] = useState(user);

  // Profile state
  const [profileForm, setProfileForm] = useState({
    name: user?.name || '',
    email: user?.email || '',
    default_hourly_rate: user?.default_hourly_rate ?? '',
    bar_number: user?.bar_number || '',
  });
  const [profileSaving, setProfileSaving] = useState(false);
  const [profileSaved, setProfileSaved]   = useState(false);

  // Reset-password state
  const [newPassword, setNewPassword]       = useState('');
  const [pwSaving,    setPwSaving]          = useState(false);
  const [pwSuccess,   setPwSuccess]         = useState('');

  // Delete-user state
  const [confirmDelete, setConfirmDelete] = useState(false);
  const [deleting,      setDeleting]      = useState(false);

  // pkg49 — rate-change scope prompt state. `ratePrompt` holds the pending
  // change ({ oldRate, newRate, payload }) while the attorney picks a scope;
  // the PATCH only fires after the choice.
  const [ratePrompt, setRatePrompt]         = useState(null);
  const [rateScope, setRateScope]           = useState('new_only');
  const [rateClients, setRateClients]       = useState(null);  // client list for 'selected'
  const [rateSelected, setRateSelected]     = useState(new Set());
  const [rateApplying, setRateApplying]     = useState(false);
  const [rateSummary, setRateSummary]       = useState('');

  // Permissions state
  const [permissions, setPermissions]   = useState(null);
  const [permsLoading, setPermsLoading] = useState(true);
  const [saving, setSaving]             = useState('');

  const [error, setError] = useState('');

  useEffect(() => {
    if (!user) return;
    setCurrentUser(user);
    setProfileForm({
      name: user.name || '',
      email: user.email || '',
      default_hourly_rate: user.default_hourly_rate ?? '',
      bar_number: user.bar_number || '',
    });
    setActiveTab('profile');
    setProfileSaved(false);
    setNewPassword('');
    setPwSuccess('');
    setConfirmDelete(false);
    setError('');
    setRatePrompt(null);      // pkg49
    setRateScope('new_only');
    setRateClients(null);
    setRateSelected(new Set());
    setRateSummary('');
  }, [user]);

  useEffect(() => {
    if (!user) return;
    let cancelled = false;
    setPermsLoading(true);
    adminApi.getUserPermissions(user.id)
      .then((data) => { if (!cancelled) { setPermissions(data.permissions); setPermsLoading(false); } })
      .catch((err) => { if (!cancelled) { setError(err.message);            setPermsLoading(false); } });
    return () => { cancelled = true; };
  }, [user]);

  function setProfile(k, v) {
    setProfileForm(prev => ({ ...prev, [k]: v }));
    setProfileSaved(false);
  }

  async function saveProfile(e) {
    e.preventDefault();
    setProfileSaving(true); setError('');
    try {
      const roleRequiresBar = currentUser.role === 'owner' || currentUser.role === 'attorney';
      const trimmedBar = (profileForm.bar_number || '').trim();
      if (roleRequiresBar && !trimmedBar) {
        setError('Bar number is required for ' + currentUser.role + ' role.');
        setProfileSaving(false);
        return;
      }
      const payload = {
        name: profileForm.name.trim(),
        email: profileForm.email.trim().toLowerCase(),
        default_hourly_rate:
          profileForm.default_hourly_rate === '' || profileForm.default_hourly_rate === null
            ? null
            : parseFloat(profileForm.default_hourly_rate),
        // Send bar_number for owners/attorneys; for staff, send null to clear it.
        bar_number: roleRequiresBar ? trimmedBar : null,
      };

      // pkg49 (D-52b) — a real rate CHANGE (old rate existed, new one differs)
      // needs a scope before it can save. Park the payload and show the
      // prompt; commitProfile() fires the PATCH once a scope is chosen.
      const oldRate = currentUser.default_hourly_rate != null && currentUser.default_hourly_rate !== ''
        ? parseFloat(currentUser.default_hourly_rate) : null;
      const newRate = payload.default_hourly_rate;
      if (oldRate != null && newRate != null && !isNaN(newRate) && oldRate !== newRate) {
        setRatePrompt({ oldRate, newRate, payload });
        setRateScope('new_only');
        setRateSelected(new Set());
        setRateSummary('');
        setProfileSaving(false);
        return;
      }

      await commitProfile(payload);
    } catch (err) {
      setError(err.message);
    } finally {
      setProfileSaving(false);
    }
  }

  // pkg49 — the actual PATCH + state refresh, shared by the plain save path
  // and the scope prompt's Apply.
  async function commitProfile(payload) {
    const updated = await adminApi.updateAdminUser(currentUser.id, payload);
    const { rate_change, ...userFields } = updated || {};
    const merged = { ...currentUser, ...userFields };
    setCurrentUser(merged);
    setProfileForm({
      name: merged.name || '',
      email: merged.email || '',
      default_hourly_rate: merged.default_hourly_rate ?? '',
      bar_number: merged.bar_number || '',
    });
    setProfileSaved(true);
    if (onUserUpdated) onUserUpdated(merged);
    return rate_change || null;
  }

  // pkg49 — lazy-load the client list the first time 'selected' is chosen.
  async function loadRateClients() {
    if (rateClients) return;
    try {
      const rows = await adminApi.listClients();
      setRateClients((rows || []).sort((a, b) => (a.name || '').localeCompare(b.name || '')));
    } catch (err) {
      setError(err.message);
    }
  }

  function toggleRateClient(id) {
    setRateSelected(prev => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id); else next.add(id);
      return next;
    });
  }

  async function applyRateChange() {
    if (!ratePrompt) return;
    setRateApplying(true); setError('');
    try {
      const payload = {
        ...ratePrompt.payload,
        rate_scope: rateScope,
        ...(rateScope === 'selected' ? { rate_client_ids: Array.from(rateSelected) } : {}),
      };
      const rc = await commitProfile(payload);
      const fmt = (n) => `$${Number(n).toFixed(2)}/hr`;
      if (rc) {
        if (rc.scope === 'new_only') {
          setRateSummary(`Rate updated to ${fmt(ratePrompt.newRate)} for new clients. ${rc.protected} existing client${rc.protected === 1 ? '' : 's'} kept ${fmt(ratePrompt.oldRate)}.`);
        } else if (rc.scope === 'all') {
          setRateSummary(`Rate updated to ${fmt(ratePrompt.newRate)}. ${rc.moved} client${rc.moved === 1 ? '' : 's'} at ${fmt(ratePrompt.oldRate)} moved with it; custom rates were not touched.`);
        } else {
          setRateSummary(`${rc.moved} selected client${rc.moved === 1 ? '' : 's'} moved to ${fmt(ratePrompt.newRate)}; ${rc.protected} other${rc.protected === 1 ? '' : 's'} kept ${fmt(ratePrompt.oldRate)}.`);
        }
      }
      setRatePrompt(null);
    } catch (err) {
      setError(err.message);
    } finally {
      setRateApplying(false);
    }
  }

  function cancelRateChange() {
    setRatePrompt(null);
    setRateSummary('');
  }

  async function handlePasswordReset(e) {
    e.preventDefault();
    setError(''); setPwSuccess('');
    if (!newPassword || newPassword.length < 8) {
      setError('Password must be at least 8 characters.');
      return;
    }
    setPwSaving(true);
    try {
      await adminApi.resetAdminPassword(currentUser.id, newPassword);
      setPwSuccess('Password updated.');
      setNewPassword('');
    } catch (err) {
      setError(err.message);
    } finally {
      setPwSaving(false);
    }
  }

  async function handleDelete() {
    setDeleting(true); setError('');
    try {
      await adminApi.deleteAdminUser(currentUser.id);
      // Tell the parent so it can remove the row and close us.
      if (onUserDeleted) onUserDeleted(currentUser.id, currentUser.name);
    } catch (err) {
      setError(err.message);
      setDeleting(false);
      setConfirmDelete(false);
    }
  }

  async function handleToggle(key, currentEnabled) {
    setSaving(key); setError('');
    try {
      const next = !currentEnabled;
      await adminApi.setUserPermission(currentUser.id, key, next);
      setPermissions((prev) => prev ? { ...prev, [key]: { enabled: next, source: 'user' } } : prev);
    } catch (err) {
      setError(err.message);
    } finally {
      setSaving('');
    }
  }

  async function handleRevert(key) {
    setSaving(key); setError('');
    try {
      await adminApi.revertUserPermission(currentUser.id, key);
      const data = await adminApi.getUserPermissions(currentUser.id);
      setPermissions(data.permissions);
    } catch (err) {
      setError(err.message);
    } finally {
      setSaving('');
    }
  }

  if (!user || !currentUser) return null;
  const isOwner = currentUser.role === 'owner';

  return (
    <div style={overlayStyle} onClick={onClose}>
      <div style={modalStyle} onClick={(e) => e.stopPropagation()}>
        {/* Header */}
        <div style={headerStyle}>
          <div style={{ flex: 1, minWidth: 0 }}>
            <div style={kickerStyle}>Manage</div>
            <div style={titleStyle}>
              {currentUser.name}
              <span style={{ opacity: 0.5, fontSize: 13, marginLeft: 8, textTransform: 'capitalize' }}>
                ({currentUser.role})
              </span>
            </div>
          </div>
          <button onClick={onClose} style={closeButtonStyle} aria-label="Close">×</button>
        </div>

        {/* Tabs */}
        <div style={tabRowStyle}>
          <button onClick={() => setActiveTab('profile')}     style={tabButtonStyle(activeTab === 'profile')}>Profile</button>
          <button onClick={() => setActiveTab('permissions')} style={tabButtonStyle(activeTab === 'permissions')}>Permissions</button>
        </div>

        {/* Body */}
        <div style={bodyStyle}>
          {error && <div className="alert alert-error" style={{ margin: '12px 0' }}>{error}</div>}

          {activeTab === 'profile' && (
            <div style={{ paddingTop: 8 }}>
              {/* ── Profile fields ───────────────────────────────────── */}
              <form onSubmit={saveProfile}>
                <div className="form-group" style={{ marginBottom: 14 }}>
                  <label>Full Name</label>
                  <input type="text" value={profileForm.name}
                    onChange={e => setProfile('name', e.target.value)} required />
                </div>

                <div className="form-group" style={{ marginBottom: 14 }}>
                  <label>Email</label>
                  <input type="email" value={profileForm.email}
                    onChange={e => setProfile('email', e.target.value)} required />
                </div>

                <div className="form-group" style={{ marginBottom: 14 }}>
                  <label>Default Hourly Rate</label>
                  <input type="number" onWheel={(e) => e.currentTarget.blur()} min="0" step="0.01" value={profileForm.default_hourly_rate}
                    onChange={e => setProfile('default_hourly_rate', e.target.value)}
                    placeholder="e.g. 300.00 (leave blank to clear)" />
                </div>

                {(currentUser.role === 'owner' || currentUser.role === 'attorney') && (
                  <div className="form-group" style={{ marginBottom: 14 }}>
                    <label>Bar Number <span style={{ color: '#B85042' }}>*</span></label>
                    <input type="text" value={profileForm.bar_number}
                      onChange={e => setProfile('bar_number', e.target.value)}
                      placeholder="e.g. BPR # 024983 or State Bar No. 123456"
                      required />
                    <div style={hintStyle}>
                      Required for {currentUser.role}s. Appears on every filing this attorney signs.
                    </div>
                  </div>
                )}

                <div className="form-group" style={{ marginBottom: 14 }}>
                  <label>Username</label>
                  <input type="text" value={currentUser.username || ''} disabled
                    style={{ background: '#f5f5f5', color: '#888', cursor: 'not-allowed' }} />
                  <div style={hintStyle}>Username can't be changed (it's the login identifier).</div>
                </div>

                <div className="form-group" style={{ marginBottom: 14 }}>
                  <label>Role</label>
                  <input type="text" value={currentUser.role || ''} disabled
                    style={{ background: '#f5f5f5', color: '#888', cursor: 'not-allowed', textTransform: 'capitalize' }} />
                  <div style={hintStyle}>Change the role from the row in Staff Management using the "change" link.</div>
                </div>

                <div style={{ display: 'flex', gap: 14, alignItems: 'center', marginTop: 18, flexWrap: 'wrap' }}>
                  <button type="submit" className="btn btn-primary" disabled={profileSaving || !!ratePrompt}>
                    {profileSaving ? 'Saving...' : 'Save Profile'}
                  </button>
                  {profileSaved && (
                    <span style={{ fontFamily: 'Arial, sans-serif', fontSize: 12, color: '#0F1838', fontWeight: 600 }}>
                      ✓ Saved
                    </span>
                  )}
                </div>
              </form>

              {/* pkg49 (D-52b) — rate-change scope prompt. Blocks the save
                  until the attorney says who the new rate applies to. */}
              {rateSummary && (
                <div className="alert alert-success" style={{ marginTop: 14 }}>{rateSummary}</div>
              )}
              {ratePrompt && (
                <div style={{ marginTop: 16, border: '1px solid #f5c842', background: '#fffdf3', borderRadius: 6, padding: '14px 16px' }}>
                  <div style={{ fontFamily: 'Arial, sans-serif', fontSize: 13, fontWeight: 700, marginBottom: 4 }}>
                    Rate change: ${ratePrompt.oldRate.toFixed(2)}/hr → ${ratePrompt.newRate.toFixed(2)}/hr
                  </div>
                  <p style={{ fontFamily: 'Arial, sans-serif', fontSize: 12.5, color: '#555', marginTop: 0, marginBottom: 12, lineHeight: 1.5 }}>
                    Who does the new rate apply to? Clients with their own custom rate are never changed automatically —
                    edit those from the client's page.
                  </p>

                  <label style={rateOptStyle}>
                    <input type="radio" name="rateScope" checked={rateScope === 'new_only'}
                      onChange={() => setRateScope('new_only')} style={{ marginTop: 3 }} />
                    <span>
                      <strong>New clients only.</strong>{' '}
                      Everyone currently under contract keeps ${ratePrompt.oldRate.toFixed(2)}/hr — clients without a
                      rate of their own are stamped at it so they can't drift.
                    </span>
                  </label>

                  <label style={rateOptStyle}>
                    <input type="radio" name="rateScope" checked={rateScope === 'all'}
                      onChange={() => setRateScope('all')} style={{ marginTop: 3 }} />
                    <span>
                      <strong>All clients at the old rate, going forward + new clients.</strong>{' '}
                      Clients currently at ${ratePrompt.oldRate.toFixed(2)}/hr move to ${ratePrompt.newRate.toFixed(2)}/hr.
                      Time already entered is never recalculated.
                    </span>
                  </label>

                  <label style={rateOptStyle}>
                    <input type="radio" name="rateScope" checked={rateScope === 'selected'}
                      onChange={() => { setRateScope('selected'); loadRateClients(); }} style={{ marginTop: 3 }} />
                    <span>
                      <strong>Let me pick which clients move</strong> — everyone I don't pick keeps ${ratePrompt.oldRate.toFixed(2)}/hr.
                    </span>
                  </label>

                  {rateScope === 'selected' && (
                    <div style={{ margin: '10px 0 4px 24px' }}>
                      {!rateClients ? (
                        <div style={{ fontFamily: 'Arial, sans-serif', fontSize: 12, color: '#888' }}>Loading clients…</div>
                      ) : (
                        <>
                          <button type="button"
                            onClick={() => setRateSelected(prev =>
                              prev.size === rateClients.length ? new Set() : new Set(rateClients.map(c => c.id)))}
                            style={{ background: 'none', border: 'none', padding: 0, marginBottom: 6, color: '#2f5496',
                              fontSize: 11.5, fontFamily: 'Arial, sans-serif', cursor: 'pointer', textDecoration: 'underline' }}>
                            {rateSelected.size === rateClients.length ? 'Clear all' : 'Select all'}
                          </button>
                          <div style={{ maxHeight: 180, overflowY: 'auto', border: '1px solid #eee', borderRadius: 4, background: '#fff' }}>
                            {rateClients.map((c, idx) => (
                              <label key={c.id} style={{ display: 'flex', gap: 8, alignItems: 'center', padding: '6px 10px',
                                borderBottom: idx < rateClients.length - 1 ? '1px solid #f2f2f2' : 'none',
                                fontFamily: 'Arial, sans-serif', fontSize: 12.5, cursor: 'pointer' }}>
                                <input type="checkbox" checked={rateSelected.has(c.id)} onChange={() => toggleRateClient(c.id)} />
                                <span style={{ flex: 1, minWidth: 0, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{c.name}</span>
                                <span style={{ color: '#888', fontSize: 11 }}>
                                  {c.hourly_rate != null && c.hourly_rate !== '' ? `$${parseFloat(c.hourly_rate).toFixed(2)}/hr` : 'no rate set'}
                                </span>
                              </label>
                            ))}
                            {!rateClients.length && (
                              <div style={{ padding: 10, fontFamily: 'Arial, sans-serif', fontSize: 12, color: '#888' }}>No active clients.</div>
                            )}
                          </div>
                        </>
                      )}
                    </div>
                  )}

                  <div style={{ display: 'flex', gap: 8, marginTop: 12 }}>
                    <button type="button" className="btn" onClick={cancelRateChange} disabled={rateApplying}>Cancel</button>
                    <button type="button" className="btn btn-primary" onClick={applyRateChange}
                      disabled={rateApplying || (rateScope === 'selected' && !rateClients)}>
                      {rateApplying ? 'Applying…' : 'Apply Rate Change'}
                    </button>
                  </div>
                </div>
              )}

              {/* ── Reset password ───────────────────────────────────── */}
              <div style={sectionDivider}>Reset Password</div>
              <p style={sectionDesc}>
                Set a new temporary password for {currentUser.name}. Deliver it to them directly — in person or by phone.
              </p>
              {pwSuccess && (
                <div className="alert alert-success" style={{ marginBottom: 12 }}>{pwSuccess}</div>
              )}
              <form onSubmit={handlePasswordReset}>
                <div style={{ display: 'flex', gap: 8, alignItems: 'flex-end', flexWrap: 'wrap' }}>
                  <div className="form-group" style={{ flex: 1, minWidth: 220, marginBottom: 0 }}>
                    <label>New Password</label>
                    <input type="text" value={newPassword}
                      onChange={e => { setNewPassword(e.target.value); setPwSuccess(''); }}
                      placeholder="At least 8 characters" />
                  </div>
                  <button type="submit" className="btn" disabled={pwSaving || !newPassword}>
                    {pwSaving ? 'Resetting...' : 'Reset Password'}
                  </button>
                </div>
              </form>

              {/* ── Danger zone ──────────────────────────────────────── */}
              <div style={sectionDivider}>
                <span style={{ color: '#B85042' }}>Danger Zone</span>
              </div>
              <p style={sectionDesc}>
                Permanently remove {currentUser.name} from the portal. They'll lose access immediately and any time they've logged will remain on prior invoices, but they can no longer be assigned to new work.
                {isOwner && ' The system protects against removing the last owner.'}
              </p>
              {!confirmDelete ? (
                <button
                  className="btn"
                  style={{ borderColor: '#B85042', color: '#B85042' }}
                  onClick={() => setConfirmDelete(true)}
                  disabled={deleting}>
                  Remove User
                </button>
              ) : (
                <div style={{
                  padding: 12,
                  border: '1px solid #B85042',
                  borderRadius: 4,
                  background: '#fdf0ef',
                }}>
                  <p style={{
                    fontFamily: 'Arial, sans-serif', fontSize: 13, color: '#B85042',
                    margin: '0 0 12px 0', fontWeight: 500,
                  }}>
                    Are you sure? Remove {currentUser.name} from the portal? This cannot be undone.
                  </p>
                  <div style={{ display: 'flex', gap: 8 }}>
                    <button
                      className="btn"
                      onClick={() => setConfirmDelete(false)}
                      disabled={deleting}>
                      Cancel
                    </button>
                    <button
                      onClick={handleDelete}
                      disabled={deleting}
                      style={{
                        background: '#B85042', color: '#fff', border: 'none',
                        borderRadius: 3, padding: '8px 16px', cursor: deleting ? 'default' : 'pointer',
                        fontFamily: 'Arial, sans-serif', fontSize: 13, fontWeight: 500,
                      }}>
                      {deleting ? 'Removing...' : 'Yes, Remove'}
                    </button>
                  </div>
                </div>
              )}
            </div>
          )}

          {activeTab === 'permissions' && (
            <>
              {isOwner && (
                <div className="alert alert-info" style={{ margin: '12px 0' }}>
                  <strong>Owner</strong> — Always has full access to everything. Cannot be restricted.
                </div>
              )}

              {permsLoading ? (
                <div style={{ padding: 20, fontSize: 13, fontFamily: 'Arial, sans-serif', color: '#888' }}>Loading...</div>
              ) : (
                <div style={{ marginTop: 8, border: '1px solid #eee', borderRadius: 4, overflow: 'hidden' }}>
                  {PERMISSIONS.map(({ key, label, desc }, i) => {
                    const p = permissions?.[key];
                    if (!p) return null;
                    const isOverride = p.source === 'user';
                    const isSaving = saving === key;
                    return (
                      <div key={key} style={{
                        display: 'flex',
                        alignItems: 'flex-start',
                        gap: 12,
                        padding: '12px 14px',
                        borderBottom: i < PERMISSIONS.length - 1 ? '1px solid #eee' : 'none',
                        background: i % 2 === 0 ? '#fff' : '#fafafa',
                      }}>
                        <div style={{ flex: 1, minWidth: 0 }}>
                          <div style={{ fontFamily: 'Arial, sans-serif', fontSize: 13, fontWeight: 500 }}>{label}</div>
                          <div style={{ fontFamily: 'Arial, sans-serif', fontSize: 11, color: '#888', marginTop: 2 }}>{desc}</div>
                          <div style={{ height: 22, marginTop: 4 }}>
                            {isOverride && !isOwner && (
                              <button
                                onClick={() => handleRevert(key)}
                                disabled={isSaving}
                                style={{
                                  background: 'none', border: 'none', padding: 0,
                                  color: '#2f5496', fontSize: 11, fontFamily: 'Arial, sans-serif',
                                  cursor: isSaving ? 'default' : 'pointer', textDecoration: 'underline',
                                }}>
                                Revert to role default
                              </button>
                            )}
                          </div>
                        </div>
                        <div style={{ flexShrink: 0, textAlign: 'center', width: 70 }}>
                          <button
                            onClick={() => handleToggle(key, p.enabled)}
                            disabled={isOwner || isSaving}
                            style={{
                              width: 52, height: 28, borderRadius: 14,
                              background: p.enabled ? '#0F1838' : '#ddd',
                              border: 'none',
                              cursor: (isOwner || isSaving) ? 'not-allowed' : 'pointer',
                              position: 'relative', transition: 'background 0.2s',
                              opacity: isOwner ? 0.6 : 1,
                            }}>
                            <span style={{
                              position: 'absolute', top: 4,
                              left: p.enabled ? 28 : 4,
                              width: 20, height: 20, borderRadius: '50%',
                              background: '#fff', transition: 'left 0.2s',
                              boxShadow: '0 1px 3px rgba(0,0,0,0.2)',
                            }} />
                          </button>
                          <div style={{ fontFamily: 'Arial, sans-serif', fontSize: 10, color: p.enabled ? '#0F1838' : '#aaa', marginTop: 4, fontWeight: 600 }}>
                            {isSaving ? '...' : p.enabled ? 'Allowed' : 'Denied'}
                          </div>
                        </div>
                      </div>
                    );
                  })}
                </div>
              )}
            </>
          )}
        </div>

        {/* Footer */}
        <div style={footerStyle}>
          <button onClick={onClose} className="btn btn-primary">Done</button>
        </div>
      </div>
    </div>
  );
}

// ----- styles -----
function tabButtonStyle(active) {
  return {
    padding: '10px 18px',
    border: 'none',
    background: 'none',
    cursor: 'pointer',
    fontFamily: 'Arial, sans-serif',
    fontSize: 13,
    fontWeight: active ? 600 : 400,
    color: active ? '#0F1838' : '#888',
    borderBottom: active ? '2px solid #0F1838' : '2px solid transparent',
    marginBottom: -1,
  };
}

const overlayStyle = {
  position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.5)',
  display: 'flex', alignItems: 'center', justifyContent: 'center',
  zIndex: 1000, padding: 8,
};
const modalStyle = {
  background: '#fff', borderRadius: 6, width: '100%', maxWidth: 680,
  maxHeight: '92vh', display: 'flex', flexDirection: 'column',
  boxShadow: '0 8px 32px rgba(0,0,0,0.3)',
};
const headerStyle = {
  background: '#0F1838', padding: '14px 16px', borderRadius: '6px 6px 0 0',
  display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start',
  gap: 10,
};
const kickerStyle = {
  fontSize: 11, fontFamily: 'Arial, sans-serif',
  textTransform: 'uppercase', letterSpacing: '0.08em',
  color: 'rgba(255,255,255,0.6)',
};
const titleStyle = {
  fontSize: 17, fontFamily: 'Arial, sans-serif', color: '#fff', marginTop: 4,
  wordBreak: 'break-word',
};
const closeButtonStyle = {
  background: 'transparent', border: 'none', color: 'rgba(255,255,255,0.7)',
  fontSize: 28, lineHeight: 1, cursor: 'pointer', padding: 0, width: 32,
  flexShrink: 0,
};
const tabRowStyle = {
  display: 'flex',
  borderBottom: '1px solid #eee',
  paddingLeft: 14,
};
const bodyStyle = {
  padding: '8px 16px 16px', overflowY: 'auto', flex: 1,
};
const footerStyle = {
  padding: '12px 16px', borderTop: '1px solid #eee',
  display: 'flex', justifyContent: 'flex-end',
};
const hintStyle = {
  fontFamily: 'Arial, sans-serif', fontSize: 11, color: '#888', marginTop: 4,
};
// Visual divider with a label, used between Profile / Reset / Danger sections.
const sectionDivider = {
  marginTop: 28,
  paddingTop: 14,
  borderTop: '1px solid #eee',
  fontFamily: 'Arial, sans-serif',
  fontSize: 11,
  fontWeight: 700,
  textTransform: 'uppercase',
  letterSpacing: '0.06em',
  color: '#0F1838',
  marginBottom: 6,
};
const sectionDesc = {
  fontFamily: 'Arial, sans-serif', fontSize: 13, color: '#555',
  marginTop: 0, marginBottom: 12, lineHeight: 1.5,
};
// pkg49 — scope-prompt radio row.
const rateOptStyle = {
  display: 'flex', gap: 9, alignItems: 'flex-start',
  fontFamily: 'Arial, sans-serif', fontSize: 12.5, lineHeight: 1.5,
  marginBottom: 8, cursor: 'pointer',
};
